import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Box,
  VStack,
  Text,
  useToast,
  Spinner,
  FormErrorMessage,

} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { sendUserMessage } from '../../Redux/Login/action';
import { searchCommunityUsers } from '../../Redux/Mode/action';

const SendMessageModal = ({ isOpen, onClose, initialRecipient = null }) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { sendingMessage, sendError } = useSelector(state => state.auth.messages || {});
  const [recipientId, setRecipientId] = useState('');
  const [message, setMessage] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);  const [errors, setErrors] = useState({});

  
  // Set initial recipient if provided
  useEffect(() => {
    if (initialRecipient) {
      setRecipientId(initialRecipient);
    }
  }, [initialRecipient]);
  
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (!initialRecipient) {
        setRecipientId('');
      }
      setMessage('');
      setErrors({});
      setSearchResults([]);
    }
  }, [isOpen, initialRecipient]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!recipientId) {
      newErrors.recipientId = 'Recipient is required';
    }
    
    if (!message) {
      newErrors.message = 'Message cannot be empty';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleRecipientChange = async (e) => {
    const value = e.target.value;
    setRecipientId(value);
    setErrors(prev => ({ ...prev, recipientId: null }));
    
    // Only search if we have at least 2 characters
    if (value.length >= 2) {
      setIsSearching(true);
      try {
        // Use the community user search instead of admin search
        const results = await dispatch(searchCommunityUsers({ 
          query: value,
          page: 1,
          limit: 5 
        }));
        
        if (results && results.users) {
          setSearchResults(results.users);
        }
      } catch (error) {
        console.error("User search error:", error);
      }
      setIsSearching(false);
    } else {
      setSearchResults([]);
    }
  };
  
  const selectUser = (burrowerId) => {
    setRecipientId(burrowerId);
    setSearchResults([]);
  };
  
  const handleSendMessage = async () => {
    if (!validateForm()) return;
    
    const result = await dispatch(sendUserMessage(recipientId, message));
    
    if (result.success) {
      toast({
        title: "Message Sent",
        description: `Your message has been sent to ${recipientId}`,
        status: "success",
        duration: 3000,
      });
      onClose();
    } else {
      // Phase 6: Show specific messaging gate / block errors
      const errorMsg = result.error || "Failed to send message";
      const isPrivacyBlock = errorMsg.includes('block') || errorMsg.includes('privacy') || errorMsg.includes('does not accept');
      toast({
        title: isPrivacyBlock ? "Message Not Allowed" : "Error",
        description: errorMsg,
        status: isPrivacyBlock ? "warning" : "error",
        duration: 5000,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg="gray.800" color="white">
        <ModalHeader>Send Message</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4}>
            <FormControl isInvalid={!!errors.recipientId}>
              <FormLabel>Recipient</FormLabel>
              <Input
                placeholder="Enter recipient's Burrower ID"
                value={recipientId}
                onChange={handleRecipientChange}
                disabled={!!initialRecipient}
              />
              {errors.recipientId && (
                <FormErrorMessage>{errors.recipientId}</FormErrorMessage>
              )}
              
              {/* User search results */}
              {isSearching && (
                <Box mt={2} textAlign="center">
                  <Spinner size="sm" />
                </Box>
              )}
              
              {searchResults.length > 0 && (
                <Box 
                  mt={2} 
                  border="1px" 
                  borderColor="gray.200" 
                  borderRadius="md"
                >
                  {searchResults.map(user => (
                    <Box 
                      key={user._id} 
                      p={2}
                      _hover={{ bg: "gray.50" }}
                      cursor="pointer"
                      onClick={() => selectUser(user.burrowerId)}
                    >
                      <Text>{user.burrowerId}</Text>
                    </Box>
                  ))}
                </Box>
              )}
            </FormControl>
            
            <FormControl isInvalid={!!errors.message}>
              <FormLabel>Message</FormLabel>
              <Textarea
                placeholder="Type your message here..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
              />
              {errors.message && (
                <FormErrorMessage>{errors.message}</FormErrorMessage>
              )}
            </FormControl>
            
            {sendError && (
              <Box p={3} bg="red.900" color="red.300" borderRadius="md" width="100%">
                <Text>{sendError}</Text>
              </Box>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button 
            colorScheme="blue" 
            onClick={handleSendMessage}
            isLoading={sendingMessage}
          >
            Send Message
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default SendMessageModal;
